import { apiClient, type PaginatedResponse } from '../../../api/client'
import { buildPaginationParams, type PaginationOptions } from '../../../api/pagination'
import { ItemssharepointApi } from '../../../generated-types/apis/ItemssharepointApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import { createSharepoint } from '../../Item/utils/functions'
import { type Sharepoint } from '../../Item/utils/types'

const log = new LogTool({ context: 'SharepointAPI', enable: true, logLevel: 'warn' })

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    }
  })
}

/**
 * Create API instance
 */
const sharepointsApi = new ItemssharepointApi(getApiConfiguration())

export type UnreadFeedbackSubmissionCount = {
  total: number
  articleShares: number
  BOMShares: number
  guestRequestShares: number
  requestShares: number
}

/**
 * Fetch unread feedback submission count (detailed breakdown)
 * Note: Uses apiClient for custom aggregation endpoint
 */
export async function fetchUnreadFeedbackSubmissionCount(): Promise<UnreadFeedbackSubmissionCount> {
  log.info('Fetching unread feedback submission count')

  const response = await apiClient.get<any>(
    'v1/items/sharepoints/unread_feedback_submission_count/'
  )
  
  return {
    total: response.total || 0,
    articleShares: response.article_shares || 0,
    BOMShares: response.bom_shares || 0,
    guestRequestShares: response.guest_request_shares || 0,
    requestShares: response.request_shares || 0,
  }
}

/**
 * Fetch sharepoint data
 * Note: Uses apiClient for expand parameters
 */
export async function fetchSharepoint(sharepointId: string | number): Promise<any> {
  log.info('Fetching sharepoint:', sharepointId)

  const url = typeof sharepointId === 'number' || !String(sharepointId).includes('/')
    ? `v1/items/sharepoints/${sharepointId}/`
    : String(sharepointId)

  const response = await apiClient.get<any>(url)
  return createSharepoint(response)
}

/**
 * Fetch all sharepoints with pagination
 */
export async function fetchSharepoints(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Sharepoint>> {
  log.info('Fetching sharepoints with options:', options)

  const response = await sharepointsApi.v1ItemsSharepointsList({
    page: options.page,
    pageSize: options.pageSize,
  })

  const sharepoints = response.results ? response.results.map(createSharepoint) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: sharepoints,
  }
}

/**
 * Create a sharepoint
 */
export async function createSharepointApi(sharepointData: any): Promise<Sharepoint> {
  log.info('Creating sharepoint:', sharepointData)

  const response = await sharepointsApi.v1ItemsSharepointsCreate({
    sharepointCreationRequest: sharepointData,
  } as any)
  return createSharepoint(response)
}

/**
 * Update a sharepoint
 */
export async function updateSharepointApi(
  idOrSelf: string | number,
  sharepointData: any
): Promise<Sharepoint> {
  log.info('Updating sharepoint:', idOrSelf)

  const url = idOrSelf.toString().includes('/')
    ? String(idOrSelf)
    : `v1/items/sharepoints/${idOrSelf}/`

  // Use apiClient to avoid any generated-model serialization quirks and preserve payload keys as-is
  const response = await apiClient.patch<any>(url, sharepointData)
  return createSharepoint(response)
}

/**
 * Delete a sharepoint
 */
export async function deleteSharepointApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting sharepoint:', idOrSelf)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    await sharepointsApi.v1ItemsSharepointsDestroy({ id: idOrSelf })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/items/sharepoints/${idOrSelf}/`
  await apiClient.delete(url)
}

/**
 * Mark feedback submissions as read
 * Note: Uses apiClient for custom action endpoint
 */
export async function markFeedbackSubmissionsAsRead(sharepointId: string | number): Promise<void> {
  log.info('Marking feedback submissions as read for sharepoint:', sharepointId)

  const url = typeof sharepointId === 'number' || !String(sharepointId).includes('/')
    ? `v1/items/sharepoints/${sharepointId}/mark_feedback_submissions_as_read/`
    : `${sharepointId}mark_feedback_submissions_as_read/`

  await apiClient.post<any>(url, {})
}

/**
 * Fetch request shares (sharepoints filtered by type)
 * Uses the dedicated backend endpoint for request shares
 */
export async function fetchRequestSharesApi(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Sharepoint>> {
  log.info('Fetching request shares with options:', options)

  const params = buildPaginationParams(options)
  const url = params.length > 0
    ? `v1/items/sharepoints/request_shares/?${params.join('&')}`
    : `v1/items/sharepoints/request_shares/`

  const response = await apiClient.get<PaginatedResponse<any>>(url)
  const sharepoints = response.results.map(createSharepoint)

  return {
    ...response,
    results: sharepoints,
  }
}

/**
 * Fetch BOM shares (sharepoints filtered by type)
 * Uses the dedicated backend endpoint for BOM shares
 */
export async function fetchBOMSharesApi(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Sharepoint>> {
  log.info('Fetching BOM shares with options:', options)

  const params = buildPaginationParams(options)
  const url = params.length > 0
    ? `v1/items/sharepoints/bom_shares/?${params.join('&')}`
    : `v1/items/sharepoints/bom_shares/`

  const response = await apiClient.get<PaginatedResponse<any>>(url)
  const sharepoints = response.results.map(createSharepoint)

  return {
    ...response,
    results: sharepoints,
  }
}

/**
 * Fetch guest request shares (sharepoints filtered by type)
 * Uses the dedicated backend endpoint for guest request shares
 */
export async function fetchGuestRequestSharesApi(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Sharepoint>> {
  log.info('Fetching guest request shares with options:', options)

  const params = buildPaginationParams(options)
  const url = params.length > 0
    ? `v1/items/sharepoints/guest_request_shares/?${params.join('&')}`
    : `v1/items/sharepoints/guest_request_shares/`

  const response = await apiClient.get<PaginatedResponse<any>>(url)
  const sharepoints = response.results.map(createSharepoint)

  return {
    ...response,
    results: sharepoints,
  }
}

/**
 * Fetch article shares (sharepoints filtered by type)
 * Uses the dedicated backend endpoint for article shares
 */
export async function fetchArticleSharesApi(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Sharepoint>> {
  log.info('Fetching article shares with options:', options)

  const params = buildPaginationParams(options)
  const url = params.length > 0
    ? `v1/items/sharepoints/article_shares/?${params.join('&')}`
    : `v1/items/sharepoints/article_shares/`

  const response = await apiClient.get<PaginatedResponse<any>>(url)
  const sharepoints = response.results.map(createSharepoint)

  return {
    ...response,
    results: sharepoints,
  }
}
